import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, MapPin, Calendar, ArrowRight, ChevronRight, Bike, Footprints, Medal, Car, Moon, Heart, Mountain, Zap, Route as RouteIcon, Timer, Target, Wind, Flag, CircleDot, Dumbbell, Swords, Users, Repeat, Flame, Snowflake, Waves, Music, Compass, Sun, Sparkles, Shield, ArrowUp, BarChart3, CreditCard, ShoppingBag, LayoutDashboard, Check, QrCode, Ticket, TrendingUp, Trophy, PersonStanding, Gauge, TreePine, Diamond, Gem, Aperture } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import EventCard from '../components/EventCard';
import SponsorStripBanner from '../components/SponsorStripBanner';
import FranceRegionsMap from '../components/FranceRegionsMap';
import showcaseEvents from '../data/showcaseEvents';

import { eventsApi, categoriesApi } from '../services/api';

const TennisRacquet = ({ className, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <circle cx="12" cy="9" r="7" />
    <line x1="12" y1="16" x2="12" y2="23" />
    <line x1="9" y1="5" x2="9" y2="13" /><line x1="15" y1="5" x2="15" y2="13" />
    <line x1="6" y1="8" x2="18" y2="8" /><line x1="6" y1="11" x2="18" y2="11" />
  </svg>
);

const BowlingPin = ({ className, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M12 2c-1.5 0-2.5 1-2.5 2.5 0 1 .5 1.8 1 2.5-.8 1-1.5 2.5-1.5 4 0 1 .3 2 .8 2.8-.5 1.2-.8 2.5-.8 3.7 0 2.5 1.3 4.5 3 4.5s3-2 3-4.5c0-1.2-.3-2.5-.8-3.7.5-.8.8-1.8.8-2.8 0-1.5-.7-3-1.5-4 .5-.7 1-1.5 1-2.5C14.5 3 13.5 2 12 2z" />
  </svg>
);

const sportIcons = {
  cycling: Bike,
  running: Footprints,
  triathlon: Medal,
  walking: PersonStanding,
  motorsport: Car,
  rallye: Gauge,
  vtt: Mountain,
  bmx: Zap,
  cyclocross: TreePine,
  racquet: TennisRacquet,
  archery: Target,
  kitesurf: Wind,
  golf: Flag,
  petanque: CircleDot,
  billard: Diamond,
  bowling: BowlingPin,
  crossfit: Dumbbell,
  combat: Swords,
  hyrox: Flame
};

const heroImages = [
  'https://images.unsplash.com/photo-1766970096430-204f27f6e247?w=1200&q=75',
  'https://images.unsplash.com/photo-1717007518835-7975ef78dd2a?w=1200&q=80',
  'https://images.unsplash.com/photo-1758301059007-0726677b55bb?w=1200&q=80',
  'https://images.unsplash.com/photo-1771094459586-cb8d18cefc6f?w=1200&q=80',
  'https://images.pexels.com/photos/36534261/pexels-photo-36534261.jpeg?auto=compress&cs=tinysrgb&w=1200',
  'https://images.unsplash.com/photo-1753689243063-0cf0cf073707?w=1200&q=80'
];

const HomePage = () => {
  const { t } = useTranslation();
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [regionEventCounts, setRegionEventCounts] = useState({});

  useEffect(() => {
    const fallbackCategories = [
      {id: "cycling", name: "Cyclisme", icon: "bike", count: 0},
      {id: "triathlon", name: "Triathlon", icon: "medal", count: 0},
      {id: "running", name: "Course a pied", icon: "footprints", count: 0},
      {id: "walking", name: "Marche", icon: "walking", count: 0},
      {id: "motorsport", name: "Sports Mecaniques", icon: "car", count: 0},
      {id: "rallye", name: "Rallye Voitures", icon: "car", count: 0},
      {id: "vtt", name: "VTT", icon: "mountain", count: 0},
      {id: "bmx", name: "BMX", icon: "bike", count: 0},
      {id: "cyclocross", name: "Cyclo-cross", icon: "bike", count: 0},
      {id: "racquet", name: "Sports de raquette", icon: "target", count: 0},
      {id: "archery", name: "Tir a l'arc", icon: "target", count: 0},
      {id: "kitesurf", name: "Kitesurf", icon: "wind", count: 0},
      {id: "golf", name: "Golf", icon: "flag", count: 0},
      {id: "petanque", name: "Petanque", icon: "circle", count: 0},
      {id: "billard", name: "Billard", icon: "circle", count: 0},
      {id: "bowling", name: "Bowling", icon: "circle", count: 0},
      {id: "crossfit", name: "CrossFit", icon: "dumbbell", count: 0},
      {id: "combat", name: "Sports de combat", icon: "swords", count: 0},
      {id: "hyrox", name: "HYROX", icon: "flame", count: 0},
    ];
    const fetchData = async () => {
      try {
        const [eventsRes, categoriesRes, allEventsRes] = await Promise.allSettled([
          eventsApi.getFeatured(),
          categoriesApi.getAll(),
          eventsApi.getAll({ limit: 50 })
        ]);
        if (eventsRes.status === 'fulfilled') {
          setFeaturedEvents(eventsRes.value.data.events || []);
        }
        if (categoriesRes.status === 'fulfilled' && categoriesRes.value.data.categories?.length) {
          setCategories(categoriesRes.value.data.categories);
        } else {
          setCategories(fallbackCategories);
        }
        const allEvents = allEventsRes.status === 'fulfilled' ? (allEventsRes.value.data.events || []) : [];

        // Compute region counts from all events
        const regionMap = {
          'Rhone': 'ara', 'Lyon': 'ara', 'Haute-Savoie': 'ara', 'Chamonix': 'ara',
          'Isere': 'ara', 'Hautes-Alpes': 'ara', 'Megeve': 'ara', 'Morzine': 'ara',
          'Les Gets': 'ara', 'Monteynard': 'ara', 'Guillestre': 'ara',
          'Bourg-d\'Oisans': 'ara', 'Alpe d\'Huez': 'ara', 'Briancon': 'ara',
          'Paris': 'idf', 'Ile-de-France': 'idf', 'Yvelines': 'idf', 'Hauts-de-Seine': 'idf',
          'Versailles': 'idf', 'Rambouillet': 'idf',
          'Nice': 'pac', 'Alpes-Maritimes': 'pac', 'Var': 'pac', 'Vaucluse': 'pac',
          'Gordes': 'pac', 'Frejus': 'pac', 'Canjuers': 'pac', 'Provence': 'pac',
          'Corse': 'cor', 'Ajaccio': 'cor', 'Haute-Corse': 'cor', 'Corse-du-Sud': 'cor',
          'Porticcio': 'cor',
        };
        const counts = {};
        allEvents.forEach(evt => {
          const loc = evt.location || '';
          let regionId = null;
          for (const [keyword, rid] of Object.entries(regionMap)) {
            if (loc.includes(keyword)) { regionId = rid; break; }
          }
          if (regionId) {
            counts[regionId] = (counts[regionId] || 0) + 1;
          }
        });
        setRegionEventCounts(counts);
      } catch (error) {
        console.error('Error fetching data:', error);
        setCategories(fallbackCategories);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Hero image rotation
    const interval = setInterval(() => {
      setCurrentHeroImage(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/events?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        {heroImages.map((img, idx) => (
          <motion.img
            key={idx}
            src={img}
            alt="Hero background"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: currentHeroImage === idx ? 1 : 0 }}
            transition={{ duration: 1 }}
          />
        ))}
        <div className="hero-overlay" />
        
        <div className="hero-content px-4 max-w-5xl mx-auto">
          <motion.div
            className="inline-block bg-brand/20 border border-brand/40 backdrop-blur-sm px-4 py-1.5 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <span className="font-heading font-bold uppercase tracking-widest text-xs text-brand">
              La billetterie sportive la plus intuitive
            </span>
          </motion.div>

          <motion.h1
            className="font-heading text-5xl md:text-7xl font-extrabold tracking-tight uppercase italic mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('hero.title1')}<br />
            <span className="text-brand">{t('hero.title2')}</span>
          </motion.h1>
          
          <motion.p
            className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('hero.description')}
          </motion.p>

          {/* Search Form */}
          <motion.form
            onSubmit={handleSearch}
            className="glass-panel p-4 rounded-sm max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t('hero.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="hero-search-input"
                />
              </div>
              <Button type="submit" className="btn-primary h-12" data-testid="hero-search-btn">
                {t('hero.cta')}
              </Button>
            </div>
          </motion.form>

          {/* Dual CTA for organizers */}
          <motion.div
            className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <span className="text-slate-400 text-sm">Vous organisez un evenement ?</span>
            <Link to="/register?role=organizer" data-testid="hero-organizer-cta">
              <span className="text-brand font-heading font-bold uppercase tracking-wider text-sm hover:underline inline-flex items-center gap-1.5 group">
                Creez votre evenement gratuitement
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Categories Section */}
      <section className="section-padding bg-slate-50" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">
                {t('home.categoriesSubtitle')}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mt-2">
                {t('home.categoriesTitle')}
              </h2>
            </div>
            <Link to="/events" className="hidden md:flex items-center space-x-2 text-brand font-bold uppercase tracking-wider hover:underline">
              <span>{t('home.viewAll')}</span>
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category, idx) => {
              const IconComponent = sportIcons[category.id] || Footprints;
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/events?sport_type=${category.id}`}
                    className="category-card block p-6 bg-white border border-slate-200 text-center hover:border-brand transition-all group"
                    data-testid={`category-${category.id}`}
                  >
                    <IconComponent className="category-icon w-12 h-12 mx-auto mb-4 text-brand group-hover:text-white transition-colors" />
                    <h3 className="font-heading font-bold uppercase tracking-wider text-sm mb-1">
                      {t(`sports.${category.id}`, category.name)}
                    </h3>
                    <p className="text-xs text-slate-500 group-hover:text-white/80 transition-colors">
                      {category.count} {t('map.events')}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sponsor Strip Banner on Homepage */}
      <SponsorStripBanner />

      {/* Featured Events Section - À la une */}
      <section className="section-padding" data-testid="featured-events-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">
                {t('home.featuredTitle')}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mt-2">
                {t('home.featuredSubtitle')}
              </h2>
            </div>
            <Link to="/events" className="hidden md:flex items-center space-x-2 text-brand font-bold uppercase tracking-wider hover:underline">
              <span>{t('home.viewAll')}</span>
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loader" />
            </div>
          ) : featuredEvents.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredEvents.map((event, idx) => (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <EventCard event={event} />
                  </motion.div>
                ))}
              </div>
              {/* Showcase vitrines - always visible */}
              <div className="mt-16">
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="showcase-banner">
                  <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    <strong>Apercu :</strong> Voici a quoi pourrait ressembler votre evenement sur iBookSport.
                    <Link to="/register?role=organizer" className="ml-1 text-brand font-bold hover:underline">
                      Creez le votre gratuitement
                    </Link>
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {showcaseEvents.map((event, idx) => (
                    <motion.div
                      key={event.event_id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      viewport={{ once: true }}
                    >
                      <EventCard event={event} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="showcase-banner">
                <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <strong>Apercu :</strong> Voici a quoi ressembleront vos evenements sur iBookSport.
                  <Link to="/register?role=organizer" className="ml-1 text-brand font-bold hover:underline">
                    Creez le votre gratuitement
                  </Link>
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {showcaseEvents.map((event, idx) => (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <EventCard event={event} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 text-center md:hidden">
            <Link to="/events">
              <Button className="btn-secondary">
                Voir tous les evenements
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section Participants - Score Forme */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden" data-testid="score-forme-section">
        {/* Decorative accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-brand" />
        <div className="absolute top-40 -left-20 w-64 h-64 bg-emerald-50 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 -right-20 w-64 h-64 bg-brand/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

            {/* Left: Visual mockup of the widget */}
            <motion.div
              className="flex-1 max-w-lg w-full"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              {/* Mini widget preview */}
              <div className="bg-white border border-slate-200 shadow-xl overflow-hidden">
                <div className="flex">
                  {/* Level badge preview */}
                  <div className="bg-emerald-50 border-r border-emerald-200 p-6 flex flex-col items-center justify-center w-44 flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mb-2">
                      <Flame className="w-7 h-7 text-emerald-500" />
                    </div>
                    <p className="font-heading font-extrabold uppercase tracking-wider text-xs text-emerald-500">Confirme</p>
                    <div className="flex gap-0.5 mt-1">
                      {[1,2,3,4].map(s => (
                        <svg key={s} className={`w-3 h-3 ${s <= 3 ? 'text-emerald-500' : 'text-slate-200'}`} fill={s <= 3 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                      ))}
                    </div>
                  </div>
                  {/* IMC preview */}
                  <div className="flex-1 p-6 flex items-center gap-5">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#22c55e" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 42 * 0.6} ${2 * Math.PI * 42 * 0.4}`} />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-extrabold font-heading text-emerald-600">23.7</span>
                        <span className="text-[8px] font-heading uppercase text-slate-400">IMC</span>
                      </div>
                    </div>
                    <div>
                      <p className="font-heading font-bold uppercase text-xs text-emerald-600 mb-1">Forme optimale</p>
                      <p className="text-xs text-slate-500 leading-relaxed">Vous etes dans la zone ideale ! Continuez comme ca.</p>
                    </div>
                  </div>
                </div>
                {/* Tip bar */}
                <div className="border-t bg-slate-50 px-5 py-2.5 flex items-center gap-2 text-xs text-slate-500">
                  <span>🎯</span>
                  <span><strong className="text-asphalt">Objectif :</strong> Inscrivez-vous a un evenement pour monter de niveau !</span>
                </div>
              </div>

              {/* Floating badges decoration */}
              <div className="flex gap-3 mt-4 justify-center">
                {[
                  { name: 'Debutant', icon: Target, color: 'text-slate-400 bg-slate-50 border-slate-200' },
                  { name: 'Motive', icon: Zap, color: 'text-sky-500 bg-sky-50 border-sky-200' },
                  { name: 'Regulier', icon: TrendingUp, color: 'text-emerald-500 bg-emerald-50 border-emerald-200' },
                  { name: 'Champion', icon: Trophy, color: 'text-amber-500 bg-amber-50 border-amber-200' },
                ].map((b, i) => (
                  <motion.div
                    key={b.name}
                    className={`flex items-center gap-1.5 px-3 py-1.5 border text-[10px] font-heading font-bold uppercase tracking-wider ${b.color}`}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <b.icon className="w-3 h-3" />
                    {b.name}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Text content */}
            <motion.div
              className="flex-1 max-w-lg"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-emerald-500 text-white font-heading font-bold uppercase tracking-widest text-xs px-4 py-1.5 mb-5">
                Nouveau
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight uppercase text-asphalt leading-tight mb-5">
                Votre <span className="text-brand">score forme</span><br />
                a portee de main
              </h2>
              <p className="text-slate-500 text-base leading-relaxed mb-6">
                <strong className="text-asphalt">En tant que participant</strong>, accedez a un tableau de bord innovant qui va bien au-dela d'une simple inscription. 
                Calculez automatiquement votre IMC, decouvrez votre poids ideal et votre metabolisme de base. 
                Progressez a chaque evenement et debloquez des niveaux sportifs.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  { icon: Heart, text: 'IMC calcule automatiquement avec conseils personnalises' },
                  { icon: Trophy, text: '5 niveaux a debloquer : de Debutant a Champion' },
                  { icon: TrendingUp, text: 'Poids ideal et metabolisme de base estimes' },
                  { icon: Zap, text: 'Conseils sante quotidiens pour rester motive' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: 0.3 + idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-8 h-8 bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-brand" />
                    </div>
                    <p className="text-sm text-slate-600">{item.text}</p>
                  </motion.div>
                ))}
              </div>

              <Link to="/register?role=participant">
                <Button className="btn-primary gap-2" data-testid="score-forme-cta">
                  Creer mon compte gratuit <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <p className="text-xs text-slate-400 mt-2">100% gratuit — Disponible des l'inscription</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Organisateurs - B2B Acquisition */}
      <section className="relative overflow-hidden bg-asphalt" data-testid="organizer-acquisition-section">
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #fff 0px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, #fff 0px, transparent 1px, transparent 60px)' }} />
        {/* Brand accent top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-brand text-white font-heading font-bold uppercase tracking-widest text-xs px-4 py-1.5 mb-6">
              Espace Organisateurs
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-white mt-3 leading-tight">
              Le tableau de bord<br />
              <span className="text-brand">le plus complet du marche</span>
            </h2>
            <p className="text-slate-400 mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Gerez vos inscriptions, paiements, boutique et statistiques depuis un seul outil. Inscription 100% gratuite, sans engagement.
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {[
              { icon: Ticket, title: 'Inscriptions en ligne', desc: 'Formulaires personnalises, tarifs multiples, courses et parcours illimites.', color: 'bg-brand' },
              { icon: CreditCard, title: 'Paiements securises', desc: 'Encaissez vos inscriptions via Square. Compte sequestre et virement automatique.', color: 'bg-emerald-500' },
              { icon: ShoppingBag, title: 'Boutique personnalisee', desc: 'Vendez des articles personnalises a vos participants directement depuis la plateforme.', color: 'bg-violet-500' },
              { icon: BarChart3, title: 'Statistiques temps reel', desc: 'Suivez vos ventes, inscriptions, revenus et marges en direct.', color: 'bg-sky-500' },
              { icon: QrCode, title: 'Dossards & Check-in', desc: 'Attribution automatique de numeros, verification rapide le jour J.', color: 'bg-amber-500' },
              { icon: Users, title: 'Multi-equipe', desc: 'Collaborez avec vos benevoles, prestataires et sponsors depuis un seul espace.', color: 'bg-pink-500' },
            ].map((feat, idx) => (
              <motion.div
                key={feat.title}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.06 }}
                viewport={{ once: true }}
              >
                <div className="bg-asphalt-light border border-white/[0.06] p-6 h-full hover:border-brand/40 transition-colors duration-300">
                  <div className={`w-10 h-10 ${feat.color} flex items-center justify-center mb-4`}>
                    <feat.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold uppercase tracking-wider text-sm text-white mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* How it works - 3 Steps */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white text-center mb-10">
              Lancez-vous en <span className="text-brand">3 etapes</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', title: 'Inscrivez-vous', desc: "Creez votre compte organisateur gratuitement en moins d'une minute." },
                { step: '02', title: 'Creez votre evenement', desc: 'Ajoutez vos courses, tarifs, dates et personnalisez votre page evenement.' },
                { step: '03', title: 'Gerez tout en un clic', desc: 'Inscriptions, paiements, boutique, resultats — tout est centralise.' },
              ].map((s, idx) => (
                <motion.div
                  key={s.step}
                  className="relative flex items-start gap-4 p-5 bg-white/[0.03] border border-white/[0.06]"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="font-heading text-5xl font-extrabold text-brand/20 leading-none select-none">{s.step}</span>
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-wider text-sm text-white mb-1">{s.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Checklist + CTA */}
          <motion.div
            className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="flex-1">
              <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-6">
                Pourquoi choisir <span className="text-brand">iBookSport</span> ?
              </h3>
              <ul className="space-y-3">
                {[
                  'Inscription 100% gratuite, sans engagement',
                  'Commission transparente uniquement sur les inscriptions payantes',
                  'Paiements securises avec compte sequestre',
                  'Boutique de produits derives integree',
                  'Support reactif et accompagnement personnalise',
                  'Compatible tous sports : running, trail, cyclisme, triathlon...',
                ].map((item, idx) => (
                  <motion.li
                    key={idx}
                    className="flex items-start gap-3 text-slate-300 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Check className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="bg-white/[0.03] border border-white/[0.08] p-8 lg:p-10 max-w-sm">
                <LayoutDashboard className="w-12 h-12 text-brand mx-auto mb-4" />
                <h4 className="font-heading text-xl font-bold uppercase text-white mb-2">
                  Pret a commencer ?
                </h4>
                <p className="text-slate-400 text-sm mb-6">
                  Rejoignez les organisateurs qui font confiance a iBookSport.
                </p>
                <Link to="/register?role=organizer">
                  <Button className="btn-primary w-full" data-testid="organizer-section-cta">
                    Creer mon compte gratuit
                  </Button>
                </Link>
                <p className="text-slate-500 text-xs mt-3">
                  Aucune carte bancaire requise
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* iBookSport est partout - Destinations Section */}
      <section className="relative overflow-hidden" data-testid="destinations-section">
        {/* Background */}
        <div className="absolute inset-0 bg-asphalt" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          {/* Header */}
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">
              {t('home.regionsLabel')}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mt-3 text-white">
              {t('home.regionsTitle')}
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              {t('home.regionsDescription')}
            </p>
          </motion.div>

          {/* Interactive France Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <FranceRegionsMap eventCountByRegion={regionEventCounts} />
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/events?view=map">
              <Button className="bg-transparent border-2 border-white/30 text-white hover:bg-white hover:text-asphalt font-heading font-bold uppercase tracking-wider px-8 h-12 transition-all duration-300">
                {t('home.regionsFullMap')} <MapPin className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Défis Sportifs Section */}
      <section className="section-padding" data-testid="themes-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">
              Défis sportifs
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mt-2">
              Trouvez votre défi
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Running */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-brand text-white">
                <Footprints className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Running</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Marathon', icon: Footprints, color: 'text-brand' },
                  { id: 'Trail', icon: Mountain, color: 'text-emerald-600' },
                  { id: 'Ultra-trail', icon: RouteIcon, color: 'text-violet-600' },
                  { id: 'Course nocturne', icon: Moon, color: 'text-indigo-600' },
                  { id: "Course d'obstacles", icon: Zap, color: 'text-amber-600' },
                  { id: 'Course en montagne', icon: Mountain, color: 'text-emerald-700' },
                  { id: 'Course de nuit en trail', icon: Moon, color: 'text-slate-600' },
                  { id: 'Course sur sable / beach run', icon: Sun, color: 'text-yellow-600' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Autres défis course à pied */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-amber-500 text-white">
                <Users className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Autres défis course</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Course en relais', icon: Users, color: 'text-sky-600' },
                  { id: 'Ekiden', icon: Repeat, color: 'text-teal-600' },
                  { id: 'Backyard ultra', icon: Repeat, color: 'text-violet-600' },
                  { id: 'Course en escaliers', icon: ArrowUp, color: 'text-slate-600' },
                  { id: 'Course déguisée', icon: Sparkles, color: 'text-pink-600' },
                  { id: 'Course parents-enfants', icon: Heart, color: 'text-rose-500' },
                  { id: 'Canicross', icon: Heart, color: 'text-amber-600' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Multisport & Fun */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-pink-500 text-white">
                <Sparkles className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Multisport & Fun</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Run & Bike', icon: Bike, color: 'text-brand' },
                  { id: 'Bike & Run', icon: Footprints, color: 'text-emerald-600' },
                  { id: 'Trail + dégustation', icon: Mountain, color: 'text-amber-700' },
                  { id: 'Course + concert / festival', icon: Music, color: 'text-purple-600' },
                  { id: 'Course en costume / fun run', icon: Sparkles, color: 'text-pink-500' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Défis hivernaux & extrêmes */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-cyan-500 text-white">
                <Snowflake className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Hivernaux & Extrêmes</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Course en raquettes', icon: Snowflake, color: 'text-cyan-600' },
                  { id: 'Course sur glace', icon: Snowflake, color: 'text-blue-500' },
                  { id: 'Ice swim challenge', icon: Waves, color: 'text-sky-600' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Obstacles & Fitness */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-rose-500 text-white">
                <Dumbbell className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Obstacles & Fitness</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Parcours ninja', icon: Swords, color: 'text-red-600' },
                  { id: 'Bootcamp race', icon: Shield, color: 'text-slate-700' },
                  { id: 'Course militaire / commando', icon: Shield, color: 'text-green-700' },
                  { id: 'Challenge fonctionnel', icon: Dumbbell, color: 'text-rose-600' },
                  { id: 'Défi 24h fitness', icon: Timer, color: 'text-amber-600' },
                  { id: 'CrossFit', icon: Dumbbell, color: 'text-rose-500' },
                  { id: 'Hyrox', icon: Flame, color: 'text-orange-600' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Défis Vélo */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-emerald-500 text-white">
                <Bike className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Défis Vélo</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Cyclosportive', icon: Bike, color: 'text-emerald-600' },
                  { id: 'VTT', icon: Mountain, color: 'text-lime-600' },
                  { id: 'Granfondo', icon: Bike, color: 'text-emerald-700' },
                  { id: 'Randonnée cycliste', icon: Bike, color: 'text-teal-600' },
                  { id: 'Course gravel', icon: Mountain, color: 'text-stone-600' },
                  { id: 'Enduro VTT', icon: Mountain, color: 'text-green-700' },
                  { id: 'Ultra-cyclisme', icon: Bike, color: 'text-emerald-800' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Endurance Outdoor */}
            <motion.div
              className="border border-slate-200 overflow-hidden hover:border-brand/40 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }} viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 px-5 py-3 bg-sky-500 text-white">
                <Compass className="w-5 h-5" />
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Endurance Outdoor</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { id: 'Triathlon', icon: Medal, color: 'text-sky-600' },
                  { id: 'Raid aventure', icon: Compass, color: 'text-cyan-600' },
                  { id: 'Raid multisports', icon: Mountain, color: 'text-teal-600' },
                  { id: 'Trail orientation', icon: Compass, color: 'text-emerald-600' },
                  { id: 'Marche nordique chronométrée', icon: Footprints, color: 'text-sky-700' },
                  { id: 'Randonnée chronométrée', icon: Timer, color: 'text-amber-600' },
                  { id: 'Swimrun', icon: Waves, color: 'text-blue-600' },
                ].map((item) => (
                  <Link key={item.id} to={`/events?search=${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 p-2 hover:bg-asphalt/[0.05] transition-all duration-200 group" data-testid={`theme-${item.id}`}>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${item.color} group-hover:text-brand transition-colors duration-200`} />
                    <span className="font-heading font-bold uppercase tracking-wider text-[10px] leading-tight group-hover:text-asphalt transition-colors duration-200">{item.id}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* CTA Organisateur */}
            <motion.div
              className="relative overflow-hidden flex flex-col items-center justify-center text-center p-8 group cursor-pointer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35, type: 'spring', stiffness: 120 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              style={{ minHeight: '100%' }}
            >
              {/* Animated background images (same as hero) */}
              {heroImages.map((img, idx) => (
                <motion.img
                  key={idx}
                  src={img}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: currentHeroImage === idx ? 1 : 0 }}
                  transition={{ duration: 1.5 }}
                />
              ))}
              {/* Dark overlay - transitions to orange on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/85 to-asphalt/70 z-[1] transition-opacity duration-500 group-hover:opacity-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand via-brand/85 to-brand/70 z-[1] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              {/* Brand accent line - grows on hover */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-brand z-[3] transition-all duration-500 group-hover:h-2" />
              {/* Animated circles overlay - become brighter on hover */}
              <div className="absolute inset-0 z-[2] opacity-15 group-hover:opacity-30 transition-opacity duration-500">
                <motion.div
                  className="absolute -top-10 -right-10 w-40 h-40 border-[3px] border-white rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                  className="absolute -bottom-8 -left-8 w-32 h-32 border-[3px] border-white rounded-full"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 border border-white/40 rounded-full"
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
              <div className="relative z-10">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Zap className="w-10 h-10 text-brand group-hover:text-white mx-auto mb-4 transition-colors duration-500" />
                  <h3 className="font-heading text-xl md:text-2xl font-bold uppercase text-white leading-tight mb-2">
                    Votre défi n'est pas dans la liste ?
                  </h3>
                  <p className="text-white/80 group-hover:text-white text-sm mb-6 max-w-xs mx-auto transition-colors duration-500">
                    Il est peut-être temps de le créer.
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.65 }}
                  viewport={{ once: true }}
                >
                  <Link to="/organizers">
                    <Button
                      className="bg-white text-brand hover:bg-slate-100 font-heading font-bold uppercase tracking-wider px-6 h-12 text-sm gap-2 transition-all hover:shadow-lg hover:shadow-white/20"
                      data-testid="cta-devenez-organisateur"
                    >
                      Devenez organisateur <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-asphalt text-white py-24 overflow-hidden" data-testid="cta-section">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-brand/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-brand/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/30 to-transparent" />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">
              Organisateurs
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mt-4 mb-6">
              Creez votre evenement<br /><span className="text-brand">des maintenant</span>
            </h2>
            <p className="text-slate-400 text-lg mb-4 max-w-2xl mx-auto">
              Rejoignez iBookSport et gerez vos inscriptions en toute simplicite. 
              Paiement securise, numeros d'inscrit automatiques et statistiques en temps reel.
            </p>
            <p className="text-slate-500 text-sm mb-8 max-w-lg mx-auto">
              Deja plus de <span className="text-white font-semibold">100 organisateurs</span> et <span className="text-white font-semibold">500 evenements</span> nous font confiance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=organizer">
                <Button className="btn-primary" data-testid="cta-bottom-register">
                  Devenir organisateur — C'est gratuit
                </Button>
              </Link>
              <Link to="/organizers">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-asphalt font-heading font-bold uppercase tracking-wider">
                  En savoir plus
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-slate-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Événements' },
              { value: '50K+', label: 'Participants' },
              { value: '100+', label: 'Organisateurs' },
              { value: '5', label: 'Sports' }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="font-heading text-5xl md:text-6xl font-extrabold text-brand">
                  {stat.value}
                </div>
                <div className="font-heading uppercase tracking-wider text-sm text-slate-500 mt-2">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
